/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { ACCESS_CONTROL_BASE_URL, ADMIN_USER_BASE_URL } from "Constants/SagaConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IAccessControlRequest } from "Interfaces/AccessControl/IAccessControlRequest";
import { IUserRole } from "Interfaces/AccessControl/IUserRole";
import { IWriteAccessControlUser } from "Interfaces/AccessControl/IWriteAccessControlUser";
import { IWriteUpdateUserStatus } from "Interfaces/AccessControl/IWriteUpdateUserStatus";
import { IAdminPermissionsDataResult } from "Interfaces/Admin/IAdminPermissionsDataResult";
import { IApiEmptyResult, IApiResult2 } from "Interfaces/IApiResult";
import { getTenant } from "Utility/Utils";

import { get, post, put, remove, IHttpResponse } from "./ApiUtils";
import { ICreateUserResult } from "./Interfaces/AccessControl/ICreateUserResult";
import { ICreateUserRoleResult } from "./Interfaces/AccessControl/ICreateUserRoleResult";
import { IUpdateUserRoleResult } from "./Interfaces/AccessControl/IUpdateUserRoleResult";
import { IUpdateUserStatusResult } from "./Interfaces/AccessControl/IUpdateUserStatusResult";

/**
 * Create an new user.
 * @param {string} tenant the tenant.
 * @param {IWriteAccessControlUser}  adminUser the user to create.
 */
export async function createUser(
  tenant: string,
  adminUser: IWriteAccessControlUser
): Promise<IHttpResponse<IApiResult2<ICreateUserResult>>> {
  return await post<IApiResult2<ICreateUserResult>>(
    `${ACCESS_CONTROL_BASE_URL}${getTenant(TenantTypesEnum[tenant])}`,
    { adminUser },
    true
  );
}

/**
 * Update the user status.
 * @param {string}  tenant the tenant.
 * @param {IWriteUpdateUserStatus}  userStatus the user statue to update.
 */
export async function updateUserStatus(
  tenant: string,
  userStatus: IWriteUpdateUserStatus
): Promise<IHttpResponse<IApiResult2<IUpdateUserStatusResult>>> {
  return await put<IApiResult2<IUpdateUserStatusResult>>(
    `${ACCESS_CONTROL_BASE_URL}status/${getTenant(TenantTypesEnum[tenant])}`,
    { userStatus },
    true
  );
}

/**
 * Load user roles.
 * @param {email} email the user's email.
 */
export async function loadUserRoles(
  tenant: string,
  email: string
): Promise<IHttpResponse<IApiResult2<ICreateUserResult>>> {
  return await get<IApiResult2<ICreateUserResult>>(
    `${ACCESS_CONTROL_BASE_URL}${tenant}/${email}/roles`,
    true
  );
}

/**
 * Create an user role.
 * @param {string} tenant the tenant.
 * @param {IUserRole} role the user role to create.
 */
export async function createUserRole(
  tenant: string,
  role: IUserRole
): Promise<IHttpResponse<IApiResult2<ICreateUserRoleResult>>> {
  return await post<IApiResult2<ICreateUserRoleResult>>(
    `${ACCESS_CONTROL_BASE_URL}roles/${getTenant(TenantTypesEnum[tenant])}`,
    { adminUserRole: role },
    true
  );
}

/**
 * Update an user role.
 * @param {string} tenant the tenant.
 * @param {IUserRole} role the user role to create.
 */
export async function updateUserRole(
  tenant: string,
  role: IUserRole
): Promise<IHttpResponse<IApiResult2<IUpdateUserRoleResult>>> {
  return await put<IApiResult2<IUpdateUserRoleResult>>(
    `${ACCESS_CONTROL_BASE_URL}roles/${getTenant(TenantTypesEnum[tenant])}`,
    { adminUserRole: role },
    true
  );
}

/**
 * Delete user roles.
 * @param {number} userRoleId the user role id to delete.
 */
export async function deleteUserRole(userRoleId: number): Promise<IHttpResponse<IApiEmptyResult>> {
  return await remove<IApiEmptyResult>(`${ACCESS_CONTROL_BASE_URL}roles/${userRoleId}`, {}, true);
}

/**
 * Search admin user list for access control
 */
export async function searchAccessControlList(
  tenantName: string,
  searchRequest: IAccessControlRequest
): Promise<IHttpResponse<IApiResult2<IAdminPermissionsDataResult>>> {
  return await post<IApiResult2<IAdminPermissionsDataResult>>(
    ADMIN_USER_BASE_URL + "search/" + tenantName,
    searchRequest,
    true
  );
}
